/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */

import {Checkout} from "../../../typescript-library";
import braintree, {Client, HostedFields, ThreeDSecure} from "braintree-web";
import {HostedFieldFieldOptions} from "braintree-web/hosted-fields";
import React, {useState} from "react";
import Spinner from "../../utils/loader/Spinner";
import {Button, ButtonStyles, ButtonType} from "../form/Button/Button";
import {IPaymentModule, IPurchaseButtonProps, ISubmitPaymentResponse} from "./IPaymentModule";
import {INTERNAL_ERROR_TYPE, PaymentController} from "./PaymentController";
import styles from "../../checkout/steps/Payment/CreditCardContainer.module.scss";
import {purchase} from "./purchase";
import i18n from "i18next";
import {ThreeDSecureVerifyOptions, ThreeDSecureVerifyPayload} from "braintree-web/three-d-secure";
import {BraintreeDataType} from "../../cart/CartContextProvider/CartContextProvider";

export const cardNumber = "card-nr";
export const expirationDate = "expiry-date";
export const cvv = "cvv-data";

export default class CreditCardModule implements IPaymentModule {
  public readonly checkout: Checkout;
  private client?: Client;
  private instance?: HostedFields;
  private threeDSecure?: ThreeDSecure;
  private threeDSecureParameters?: ThreeDSecureVerifyOptions;
  private paymentSelected: boolean;
  private supportedCardTypes: string[] = [];

  constructor(checkout: Checkout) {
    this.checkout = checkout;
    this.paymentSelected = true;
    this.PurchaseButton = this.PurchaseButton.bind(this) as (props: IPurchaseButtonProps) => React.ReactElement;
  }

  public PurchaseButton(props: IPurchaseButtonProps): React.ReactElement {
    const [visible, setVisible] = useState<boolean>(true);
    const {gotoHandling, handleError, label} = props;

    const actionMethod = async () => {
      if (!this.threeDSecureParameters || !this.threeDSecure) {
        gotoHandling();

        return;
      }
      setVisible(false);
      try {
        this.threeDSecure.on("lookup-complete", (data, next) => {
          if (!data) {
            return;
          }
          if (!data.paymentMethod.threeDSecureInfo.liabilityShiftPossible) {
            const {status} = data.paymentMethod.threeDSecureInfo;
            let errorMessage: string = status;
            if (status === "authentication_unavailable") errorMessage = "authenticate_rejected";

            const err = new Error(errorMessage);
            err.name = INTERNAL_ERROR_TYPE.CREDIT_CARD.OnNext + "Error";
            (err as Error & {paymentMethod: ThreeDSecureVerifyPayload}).paymentMethod = data.paymentMethod;
            this.threeDSecureParameters = undefined;
            setVisible(true);
            handleError(err, "Credit card liability shift error");

            return;
          }
          if (typeof next !== "function") {
            return;
          }
          next();
        });
        this.threeDSecure.on("customer-canceled", () => {
          this.threeDSecureParameters = undefined;
          void this.checkout.put({paymentId: null});
        });
        const verifyCardResponse: ThreeDSecureVerifyPayload = await this.threeDSecure.verifyCard(
          this.threeDSecureParameters,
        );
        if (!verifyCardResponse.threeDSecureInfo.liabilityShifted) {
          this.threeDSecureParameters = undefined;

          return;
        }

        await purchase({checkout: this.checkout, nonce: verifyCardResponse.nonce});
      } catch (err) {
        (err as Error).name = INTERNAL_ERROR_TYPE.CREDIT_CARD.OnCatch + (err as Error).name;
        this.threeDSecureParameters = undefined;
        setVisible(true);
        handleError(err as Error, "Credit card purchase error");
      }
    };

    const spinner = React.createElement(Spinner, {}, null);

    return React.createElement(
      Button,
      {action: actionMethod, styles: [ButtonStyles.wide], type: visible ? ButtonType.default : ButtonType.disabled},
      visible ? label : spinner,
    );
  }

  public setAsDeselected(): void {
    this.paymentSelected = false;
  }

  public isDataComplete(): boolean {
    return !!this.threeDSecureParameters;
  }

  public async onPaymentSelect(
    cardImageRef: React.MutableRefObject<HTMLDivElement | null>,
    braintreeData: BraintreeDataType | undefined,
  ): Promise<void> {
    const {client, token} = braintreeData || (await PaymentController.initBraintreeClient());
    this.client = client;
    if (!this.paymentSelected) {
      return;
    }

    this.instance = await braintree.hostedFields.create(this.setupOptions());
    this.supportedCardTypes = await (
      this.instance as HostedFields & {getSupportedCardTypes: () => Promise<string[]>}
    ).getSupportedCardTypes();
    this.instance.on("cardTypeChange", (event: {cards: {type: string; niceType: string}[]}) => {
      if (cardImageRef.current) {
        if (event.cards.length === 1 && this.supportedCardTypes.includes(event.cards[0].niceType)) {
          cardImageRef.current.className = `${styles.cardImage} ${styles[event.cards[0].type]}`;
        } else {
          cardImageRef.current.className = styles.cardImage;
        }
      }
    });
    this.threeDSecure = await braintree.threeDSecure.create({client: this.client, authorization: token, version: 2});
  }

  public async onSubmitPayment(): Promise<ISubmitPaymentResponse> {
    if (!this.instance) {
      return {success: false, err: {code: "HOSTED_FIELDS_FIELDS_INVALID", cardType: ""}};
    }
    if (!this.instance.getState().cards.length) {
      return {success: false, err: {code: "HOSTED_FIELDS_FIELDS_INVALID", cardType: ""}};
    }
    const currentCard: string = this.instance.getState().cards[0].niceType;
    if (!this.supportedCardTypes.includes(currentCard)) {
      return {success: false, err: {code: "CARD_TYPE_NOT_SUPPORTED", cardType: currentCard}};
    }
    const tokenizeReturn = await this.instance.tokenize();
    const {details, nonce} = tokenizeReturn;
    const {bin} = details;
    const {cartData, deliveryAddress, invoiceAddress} = this.checkout;
    const {amount} = cartData.value.payable;
    if (!invoiceAddress || !deliveryAddress) {
      throw new Error("Shipping/Billing line1 format is invalid.");
    }

    this.threeDSecureParameters = {
      additionalInformation: {
        shippingAddress: {
          line3: "",
          countryCodeAlpha2: deliveryAddress.countryCode,
          extendedAddress: "",
          locality: deliveryAddress.city,
          postalCode: deliveryAddress.zip,
          region: deliveryAddress.state || "",
          streetAddress: ["GB", "FR", "US"].includes(deliveryAddress.countryCode)
            ? `${deliveryAddress.number} ${deliveryAddress.street}`
            : `${deliveryAddress.street} ${deliveryAddress.number}`,
        },
        shippingGivenName: deliveryAddress.firstName,
        shippingPhone: deliveryAddress.phone || "",
        shippingSurname: deliveryAddress.lastName,
        workPhoneNumber: "",
      },
      amount,
      billingAddress: {
        countryCodeAlpha2: invoiceAddress.countryCode,
        extendedAddress: "",
        givenName: invoiceAddress.firstName,
        locality: invoiceAddress.city,
        phoneNumber: invoiceAddress.phone || "",
        postalCode: invoiceAddress.zip,
        region: invoiceAddress.state || "",
        streetAddress: ["GB", "FR", "US"].includes(invoiceAddress.countryCode)
          ? `${invoiceAddress.number} ${invoiceAddress.street}`
          : `${invoiceAddress.street} ${invoiceAddress.number}`,
        surname: invoiceAddress.lastName,
      },
      bin,
      email: invoiceAddress.email || "",
      nonce,
    };

    return {success: true};
  }

  private setupOptions = (): IHostedFieldsOptions => {
    if (!this.client) {
      throw new Error("The braintree client has to be initialized first");
    }

    return {
      client: this.client,
      fields: {
        cvv: {
          placeholder: i18n.t("checkout.cvv"),
          container: "#" + cvv,
        },
        expirationDate: {
          placeholder: i18n.t("form.monthYear"),
          container: "#" + expirationDate,
        },
        number: {
          placeholder: i18n.t("checkout.enterCreditCardNumber"),
          container: "#" + cardNumber,
        },
      },
      styles: {
        "input": {
          "font-size": "16px",
        },
        "input.invalid": {
          color: "red",
        },
        "input.valid": {
          color: "green",
        },
      },
    };
  };
}

type IHostedFieldsOptions = {
  client: Client;
  fields: HostedFieldFieldOptions;
  styles: any;
};
